/**
 * Get video duration from blob
 * @param {Blob} videoBlob - Video blob
 * @returns {Promise<number>} - Duration in seconds
 */
export const getVideoDuration = (videoBlob) => {
  return new Promise((resolve, reject) => {
    try {
      const video = document.createElement('video');
      video.preload = 'metadata';
      
      // Set timeout to prevent hanging forever
      const timeout = setTimeout(() => {
        window.URL.revokeObjectURL(video.src);
        reject(new Error('Video duration extraction timed out after 10 seconds'));
      }, 10000);
      
      video.onloadedmetadata = () => {
        clearTimeout(timeout);
        window.URL.revokeObjectURL(video.src);
        
        // Check if duration is valid
        const duration = video.duration;
        console.log('📹 Raw video.duration:', duration, 'isFinite:', isFinite(duration));
        
        if (!isFinite(duration) || duration <= 0) {
          console.warn('⚠️ Invalid duration detected:', duration);
          reject(new Error('Invalid video duration'));
          return;
        }
        
        const roundedDuration = Math.round(duration);
        console.log('✅ Video duration extracted:', roundedDuration, 'seconds');
        resolve(roundedDuration);
      };
      
      video.onerror = (e) => {
        clearTimeout(timeout);
        window.URL.revokeObjectURL(video.src);
        console.error('❌ Video metadata load error:', e);
        reject(new Error('Failed to load video metadata'));
      };
      
      video.src = URL.createObjectURL(videoBlob);
      
      // Force load metadata
      video.load();
    } catch (error) {
      console.error('❌ Error in getVideoDuration:', error);
      reject(error);
    }
  });
};

/**
 * Check if a video blob contains audible audio (not silent).
 * Uses Web Audio API to decode the audio track and measure RMS level.
 * @param {Blob} videoBlob
 * @returns {Promise<{ hasAudio: boolean, rms: number }>}
 */
export const checkAudioPresence = (videoBlob) => {
  return new Promise((resolve, reject) => {
    const SILENCE_THRESHOLD = 0.005; // RMS below this = silent

    videoBlob.arrayBuffer().then((buffer) => {
      const audioCtx = new (window.AudioContext || window.webkitAudioContext)();
      audioCtx.decodeAudioData(buffer, (audioBuffer) => {
        audioCtx.close();
        // Average RMS across all channels
        let totalRms = 0;
        const channelCount = audioBuffer.numberOfChannels;
        for (let c = 0; c < channelCount; c++) {
          const data = audioBuffer.getChannelData(c);
          let sum = 0;
          for (let i = 0; i < data.length; i++) sum += data[i] * data[i];
          totalRms += Math.sqrt(sum / data.length);
        }
        const rms = totalRms / channelCount;
        resolve({ hasAudio: rms >= SILENCE_THRESHOLD, rms });
      }, (err) => {
        audioCtx.close();
        // If decode fails (no audio track), treat as silent
        resolve({ hasAudio: false, rms: 0 });
      });
    }).catch(reject);
  });
};

/**
 * Get video file size
 * @param {Blob} videoBlob - Video blob
 * @returns {number} - Size in bytes
 */
export const getVideoSize = (videoBlob) => {
  return videoBlob.size;
};

/**
 * Format file size for display
 * @param {number} bytes - Size in bytes
 * @returns {string} - Formatted size
 */
export const formatFileSize = (bytes) => {
  if (bytes === 0) return '0 Bytes';
  
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  
  return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
};

/**
 * Format duration for display
 * @param {number} seconds - Duration in seconds
 * @returns {string} - Formatted duration (MM:SS)
 */
export const formatDuration = (seconds) => {
  const mins = Math.floor(seconds / 60);
  const secs = seconds % 60;
  return `${mins}:${secs.toString().padStart(2, '0')}`;
};

/**
 * Check if browser supports MediaRecorder
 * @returns {boolean}
 */
export const isMediaRecorderSupported = () => {
  return !!(navigator.mediaDevices && navigator.mediaDevices.getUserMedia && window.MediaRecorder);
};

/**
 * Detect if user is on mobile device
 * @returns {boolean}
 */
export const isMobileDevice = () => {
  return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent) ||
    (navigator.maxTouchPoints && navigator.maxTouchPoints > 2);
};

/**
 * Request camera permission
 * @returns {Promise<MediaStream>}
 */
export const requestCameraPermission = async () => {
  try {
    // Check if browser supports getUserMedia
    if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
      throw new Error('Your browser does not support camera access. Please use a modern browser like Chrome, Firefox, or Safari.');
    }

    console.log('🎥 Requesting camera access...');
    
    const isMobile = isMobileDevice();
    console.log('📱 Device type:', isMobile ? 'Mobile' : 'Desktop');
    
    // Try with ideal constraints first
    let constraints = {
      video: isMobile ? {
        // Mobile: Use lower resolution to prevent camera from zooming in too close
        // Many mobile devices zoom when requesting high resolution
        width: { min: 480, ideal: 720, max: 1080 },
        height: { min: 640, ideal: 1280, max: 1920 },
        facingMode: 'user'
        // Don't force aspect ratio - let camera use its natural field of view
      } : {
        // Desktop: Landscape/Horizontal video (16:9)
        width: { ideal: 1920 },
        height: { ideal: 1080 },
        aspectRatio: { ideal: 16/9 },
        facingMode: 'user'
      },
      audio: true
    };

    try {
      const stream = await navigator.mediaDevices.getUserMedia(constraints);
      console.log('✅ Camera access granted');
      return stream;
    } catch (err) {
      // If ideal constraints fail, try basic constraints
      console.warn('⚠️ Ideal constraints failed, trying basic:', err.message);
      constraints = {
        video: true,
        audio: true
      };
      const stream = await navigator.mediaDevices.getUserMedia(constraints);
      console.log('✅ Camera access granted with basic constraints');
      return stream;
    }
  } catch (error) {
    console.error('❌ Camera access error:', error);
    if (error.name === 'NotAllowedError' || error.name === 'PermissionDeniedError') {
      throw new Error('Camera permission denied. Please click the camera icon in your browser address bar and allow access.');
    } else if (error.name === 'NotFoundError' || error.name === 'DevicesNotFoundError') {
      throw new Error('No camera found. Please connect a camera and refresh the page.');
    } else {
      throw new Error('Failed to access camera: ' + error.message);
    }
  }
};

/**
 * Stop all media tracks
 * @param {MediaStream} stream
 */
export const stopMediaStream = (stream) => {
  if (stream) {
    stream.getTracks().forEach(track => track.stop());
  }
};
